import { FiCalendar, FiMail, FiActivity, FiBell } from "react-icons/fi";
import { Student } from "../../../types";
import utils from "../../../helpers/utils";

interface StudentCardProps {
  student: Student;
  validityStatus: { color: string; label: string };
  onManageClick: () => void;
  onAvaliationClick: () => void;
  onNotificationClick: () => void;
}

function StudentCard({
  student,
  validityStatus,
  onManageClick,
  onAvaliationClick,
  onNotificationClick,
}: StudentCardProps) {
  return (
    <div className="group relative overflow-hidden rounded-xl border border-stroke bg-white p-4 shadow-sm transition-all hover:shadow-xl dark:border-strokedark dark:bg-boxdark sm:rounded-2xl sm:p-6">
      {/* Status Badge */}
      <div className="absolute right-3 top-3 sm:right-4 sm:top-4">
        <span
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold sm:gap-1.5 sm:px-3 sm:py-1 sm:text-xs ${
            validityStatus.color === "green"
              ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
              : validityStatus.color === "yellow"
                ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400"
                : validityStatus.color === "orange"
                  ? "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400"
                  : validityStatus.color === "red"
                    ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                    : "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400"
          }`}
        >
          {validityStatus.label}
        </span>
      </div>

      {/* Header do Card */}
      <div className="relative mb-4 flex items-start gap-3 pr-16 sm:mb-5 sm:gap-4 sm:pr-0">
        <div className="relative flex-shrink-0">
          <div className="absolute -inset-1 rounded-full bg-gradient-to-r from-purple-600 to-purple-600 opacity-0 blur-sm transition-opacity group-hover:opacity-75"></div>
          <div className="relative h-14 w-14 overflow-hidden rounded-full border-2 border-white shadow-md dark:border-boxdark sm:h-20 sm:w-20 sm:border-4">
            <img
              src={
                (student.user.photo
                  ? utils.getUriImage(student.user.photo)
                  : null) ||
                `https://ui-avatars.com/api/?name=${encodeURIComponent(
                  student.user.name,
                )}&background=85368c&color=fff&size=200`
              }
              alt={student.user.name}
              className="h-full w-full object-cover"
            />
          </div>
        </div>
        <div className="min-w-0 flex-1 pt-0 sm:pt-1">
          <h4 className="truncate text-base font-bold text-gray-900 dark:text-white sm:text-lg">
            {student.user.name}
          </h4>
          <p className="mt-0.5 flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400 sm:gap-1.5 sm:text-sm">
            <FiCalendar size={12} className="flex-shrink-0 sm:h-3.5 sm:w-3.5" />
            {utils.calcularIdade(
              new Date(student.user.birth_date || new Date()),
            )}{" "}
            anos
          </p>
        </div>
      </div>

      {/* Informações */}
      <div className="space-y-2 sm:space-y-2.5">
        <div className="flex items-center gap-2 rounded-lg bg-gray-50 px-2.5 py-2 dark:bg-gray-800/50 sm:px-3 sm:py-2.5">
          <FiMail className="flex-shrink-0 text-gray-400" size={14} />
          <span className="truncate text-xs text-gray-700 dark:text-gray-300 sm:text-sm">
            {student.user.email}
          </span>
        </div>

        <div className="flex flex-col gap-1 rounded-lg bg-purple-50 px-2.5 py-2 dark:bg-purple-900/20 sm:flex-row sm:items-center sm:justify-between sm:px-3 sm:py-2.5">
          <span className="text-xs font-medium text-purple-700 dark:text-purple-400 sm:text-sm">
            Validade do Plano
          </span>
          <span className="text-xs font-bold text-purple-900 dark:text-purple-300 sm:text-sm">
            {student.validity
              ? utils.formatDate(new Date(student.validity))
              : "Não definida"}
          </span>
        </div>

        <div className="grid grid-cols-4 gap-2 pt-1">
          <div className="flex flex-col items-center justify-center rounded-lg bg-gray-50 p-1.5 text-center dark:bg-gray-800/50">
            <span className="text-[10px] text-gray-500 dark:text-gray-400">
              Treinos
            </span>
            <span className="text-xs font-bold text-gray-900 dark:text-white sm:text-sm">
              {student.workouts_count || 0}
            </span>
          </div>
          <div className="flex flex-col items-center justify-center rounded-lg bg-gray-50 p-1.5 text-center dark:bg-gray-800/50">
            <span className="text-[10px] text-gray-500 dark:text-gray-400">
              Agend.
            </span>
            <span className="text-xs font-bold text-gray-900 dark:text-white sm:text-sm">
              {student.appointments_count || 0}
            </span>
          </div>
          <div className="flex flex-col items-center justify-center rounded-lg bg-gray-50 p-1.5 text-center dark:bg-gray-800/50">
            <span className="text-[10px] text-gray-500 dark:text-gray-400">
              Alim.
            </span>
            <span className="text-xs font-bold text-gray-900 dark:text-white sm:text-sm">
              {student.foods_count || 0}
            </span>
          </div>
          <div className="flex flex-col items-center justify-center rounded-lg bg-gray-50 p-1.5 text-center dark:bg-gray-800/50">
            <span className="text-[10px] text-gray-500 dark:text-gray-400">
              Aval.
            </span>
            <span className="text-xs font-bold text-gray-900 dark:text-white sm:text-sm">
              {student.avaliations_count || 0}
            </span>
          </div>
        </div>
      </div>

      {/* Botões de Ação */}
      <div className="mt-4 flex flex-col gap-2 sm:mt-5 sm:flex-row sm:items-center sm:gap-3 sm:px-2">
        <button
          onClick={onManageClick}
          className="w-full rounded-lg bg-gradient-to-r from-purple-600 to-purple-600 py-2 text-xs font-semibold text-white shadow-md transition-all hover:scale-[1.02] hover:shadow-lg hover:from-purple-500 hover:to-purple-600 sm:flex-1 sm:rounded-xl sm:py-2.5 sm:text-sm"
        >
          Gerenciar
        </button>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-2">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onAvaliationClick();
            }}
            title="Avaliação corporal"
            className="flex w-full items-center justify-center gap-2  rounded-lg bg-emerald-50 py-2.5 text-emerald-600 shadow-sm transition-all hover:scale-[1.02] hover:bg-emerald-100 hover:shadow-md hover:ring-2 hover:ring-emerald-200 dark:bg-emerald-900/25 dark:text-emerald-400 dark:hover:bg-emerald-900/40 dark:hover:ring-emerald-700 sm:w-auto sm:size-10 sm:flex-none sm:gap-0 sm:rounded-xl sm:py-0"
            aria-label="Ver avaliação corporal"
          >
            <FiActivity
              size={18}
              className="flex-shrink-0 sm:h-5 sm:w-5 md:mx-2 lg:mx-2"
            />
            <span className="text-xs font-medium sm:hidden">Avaliação</span>
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onNotificationClick();
            }}
            title="Enviar notificação"
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-amber-50 py-2.5 text-amber-600 shadow-sm transition-all hover:scale-[1.02] hover:bg-amber-100 hover:shadow-md hover:ring-2 hover:ring-amber-200 dark:bg-amber-900/25 dark:text-amber-400 dark:hover:bg-amber-900/40 dark:hover:ring-amber-700 sm:w-auto sm:size-10 sm:flex-none sm:gap-0 sm:rounded-xl sm:py-0"
            aria-label="Notificações"
          >
            <FiBell
              size={18}
              className="flex-shrink-0 sm:h-5 sm:w-5 md:mx-2 lg:mx-2"
            />
            <span className="text-xs font-medium sm:hidden">Notificação</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default StudentCard;
